// src/components/VideoProxy.js
import React, { useState, useEffect } from 'react';
import { ClipLoader } from 'react-spinners';

/**
 * Helper functions for device detection
 */
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * VideoProxy component uses a hybrid approach to display videos
 * while working around CORS and CSP issues.
 * Enhanced for iOS and mobile compatibility.
 */
function VideoProxy({ videoUrl, thumbnailUrl, autoPlay = false }) {
  const [loading, setLoading] = useState(true);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [videoType, setVideoType] = useState('video/webm');
  
  // Check for iOS and set appropriate state on mount
  useEffect(() => {
    const iosDetected = isIOS();
    setIsIOSDevice(iosDetected);
    
    // Set video type based on URL and platform
    if (videoUrl) {
      if (videoUrl.includes('.mp4')) {
        setVideoType('video/mp4');
      } else if (videoUrl.includes('.webm')) {
        setVideoType('video/webm');
      } else if (iosDetected) {
        // Default to MP4 for iOS if we can't determine from URL
        setVideoType('video/mp4');
      }
    }
  }, [videoUrl]);
  
  // Handle video loaded event
  const handleVideoLoaded = () => {
    console.log('Video loaded successfully');
    setLoading(false);
  };
  
  // Handle video error
  const handleVideoError = (error) => {
    console.error("Video error:", error);
    // Try fallback mode
    setFallbackMode(true);
    setLoading(false);
  };
  
  // Reset loading state when videoUrl changes
  useEffect(() => {
    setLoading(true);
    setFallbackMode(false);
  }, [videoUrl]);
  
  if (!videoUrl) {
    return (
      <div className="bg-gray-900 w-full h-full flex items-center justify-center">
        <p className="text-white">Video not available</p>
      </div>
    );
  }
  
  return (
    <div className="relative w-full h-full">
      {/* Primary video player */}
      {!fallbackMode ? (
        <video
          className="w-full h-full object-contain" 
          controls
          poster={thumbnailUrl}
          preload="metadata"
          autoPlay={autoPlay}
          playsInline={true}
          webkit-playsinline="true" // Additional iOS support
          x5-playsinline="true" // Tencent X5 browser support
          controlsList="nodownload" // Prevents download button in some browsers
          onLoadedData={handleVideoLoaded}
          onError={(e) => handleVideoError(e)}
          src={videoUrl}
        >
          {/* Multiple source types for better browser compatibility */}
          <source src={videoUrl} type={videoType} />
          <source src={videoUrl} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
          <source src={videoUrl} type="application/x-mpegURL" />
          Your browser doesn't support video playback.
        </video>
      ) : (
        // Enhanced fallback for iOS - direct link for iOS
        isIOSDevice ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-4 text-center">
            <p className="mb-4">This video may need to be opened directly on iOS devices.</p>
            <div className="flex flex-col gap-3">
              <a 
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded text-center"
              >
                Watch Video
              </a>
              <a 
                href={videoUrl}
                download
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded text-center"
              >
                Download Video
              </a>
            </div>
          </div>
        ) : (
          // Fallback iframe mode for non-iOS
          <iframe 
            src={videoUrl}
            className="w-full h-full border-0"
            title="Video"
            allowFullScreen
            allow="autoplay"
            onLoad={() => setLoading(false)}
            sandbox="allow-scripts allow-same-origin"
          ></iframe>
        )
      )}
      
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="text-white text-center">
            <ClipLoader size={40} color="#FFFFFF" />
            <p className="mt-2">Loading video...</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
              <button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                onClick={() => window.open(videoUrl, '_blank')}
              >
                Open in new tab
              </button>
              <a 
                href={videoUrl}
                download
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-center"
              >
                Download video
              </a>
            </div>
            {isMobile() && (
              <p className="mt-3 text-sm">
                If video doesn't load, try downloading or opening in a new tab
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoProxy;